import React, { useState, useRef } from "react";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import CustomExportModal from "./CustomExportModal";
import FieldHelp from "../help/FieldHelp";

/**
 * Reusable burger menu component for all charts
 * Provides export, reset, and fullscreen functionality
 * Provides export, reset, fullscreen, and custom data export functionality
 */
const ChartBurgerMenu = ({
  chartRef,
  chartTitle = "Chart",
  onReset,
  onAIAnalyze = null,
  chartData = null, // Data for CSV/XLS exports
  exportContainer = false, // If true, prefers html2canvas on the container to include legends/titles
  additionalActions = [],
  chartType = "generic",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const menuRef = useRef(null);

  const getFileName = (extension) => {
    return `${chartTitle.replace(/\s+/g, "_")}_${new Date().getTime()}.${extension}`;
  };

  // Helper to hide burger menus before capture and restore after
  const hideBurgerMenus = (container) => {
    const hiddenElements = [];
    const burgerButtons = container.querySelectorAll(
      'button[aria-label="Chart menu"]',
    );
    burgerButtons.forEach((button) => {
      let parent = button.parentElement;
      while (parent && parent !== container) {
        const style = window.getComputedStyle(parent);
        if (
          style.position === "absolute" ||
          parent.getAttribute("style")?.includes("position: absolute")
        ) {
          const originalDisplay = parent.style.display;
          const originalVisibility = parent.style.visibility;
          parent.style.display = "none";
          parent.style.visibility = "hidden";
          hiddenElements.push({
            element: parent,
            display: originalDisplay || "",
            visibility: originalVisibility || "",
          });
          break;
        }
        parent = parent.parentElement;
      }
      if (!parent || parent === container) {
        const originalDisplay = button.style.display;
        const originalVisibility = button.style.visibility;
        button.style.display = "none";
        button.style.visibility = "hidden";
        hiddenElements.push({
          element: button,
          display: originalDisplay || "",
          visibility: originalVisibility || "",
        });
      }
    });
    return hiddenElements;
  };

  const restoreBurgerMenus = (hiddenElements) => {
    hiddenElements.forEach(({ element, display, visibility }) => {
      element.style.display = display || "";
      element.style.visibility = visibility || "";
    });
  };

  const triggerHtml2CanvasExport = (format) => {
    const container = chartRef.current;
    if (!container) return;

    const hiddenElements = hideBurgerMenus(container);

    html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      onclone: (clonedDoc) => {
        const menus = clonedDoc.querySelectorAll(
          'button[aria-label="Chart menu"]',
        );
        menus.forEach((m) => (m.style.display = "none"));
      },
    })
      .then((canvas) => {
        restoreBurgerMenus(hiddenElements);

        if (format === "pdf") {
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF({
            orientation:
              canvas.width > canvas.height ? "landscape" : "portrait",
            unit: "px",
            format: [canvas.width, canvas.height],
          });
          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();
          const ratio = Math.min(
            pdfWidth / canvas.width,
            pdfHeight / canvas.height,
          );
          pdf.addImage(
            imgData,
            "PNG",
            0,
            0,
            canvas.width * ratio,
            canvas.height * ratio,
          );
          pdf.save(getFileName("pdf"));
        } else {
          const mimeType = format === "png" ? "image/png" : "image/jpeg";
          const quality = format === "jpg" ? 0.95 : undefined;
          const url = canvas.toDataURL(mimeType, quality);
          const link = document.createElement("a");
          link.download = getFileName(format);
          link.href = url;
          link.click();
        }
      })
      .catch((err) => {
        restoreBurgerMenus(hiddenElements);
        console.error("html2canvas export failed:", err);
      });
  };

  const handleExportPNG = () => {
    if (!chartRef?.current) return;
    setIsOpen(false);
    setTimeout(() => {
      if (exportContainer || chartRef.current.querySelector("svg")) {
        triggerHtml2CanvasExport("png");
        return;
      }
      const canvas = chartRef.current.querySelector("canvas");
      if (canvas) {
        const url = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.download = getFileName("png");
        link.href = url;
        link.click();
      } else {
        triggerHtml2CanvasExport("png");
      }
    }, 100);
  };

  const handleExportJPG = () => {
    if (!chartRef?.current) return;
    setIsOpen(false);
    setTimeout(() => {
      if (exportContainer || chartRef.current.querySelector("svg")) {
        triggerHtml2CanvasExport("jpg");
        return;
      }
      const canvas = chartRef.current.querySelector("canvas");
      if (canvas) {
        const url = canvas.toDataURL("image/jpeg", 0.95);
        const link = document.createElement("a");
        link.download = getFileName("jpg");
        link.href = url;
        link.click();
      } else {
        triggerHtml2CanvasExport("jpg");
      }
    }, 100);
  };

  const handleExportPDF = () => {
    if (!chartRef?.current) return;
    setIsOpen(false);
    setTimeout(() => {
      if (exportContainer || chartRef.current.querySelector("svg")) {
        triggerHtml2CanvasExport("pdf");
        return;
      }
      const canvas = chartRef.current.querySelector("canvas");
      if (canvas) {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: canvas.width > canvas.height ? "landscape" : "portrait",
          unit: "px",
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save(getFileName("pdf"));
      } else {
        triggerHtml2CanvasExport("pdf");
      }
    }, 100);
  };

  const prepareExportData = () => {
    if (!chartData) return null;

    // 1. Grid/Matrix format (Heatmap)
    if (chartData.rows && chartData.columns) {
      const header = ["", ...chartData.columns];
      const rows = chartData.rows.map((r) => [r.label, ...r.values]);
      return [header, ...rows];
    }

    // 2. Multi-series format (Analytics/Comparison)
    if (chartData.labels && chartData.datasets) {
      const header = ["Timestamp", ...chartData.datasets.map((d) => {
        let cleanLabel = d.label.replace(/\s*\([^)]*\)\s*$/, '').trim();
        return chartData.unit ? `${cleanLabel} (${chartData.unit})` : d.label;
      })];
      const rows = chartData.labels.map((label, idx) => {
        return [label, ...chartData.datasets.map((d) => d.data[idx])];
      });
      return [header, ...rows];
    }

    // 3. Simple Series format (Solar Graph / Pie)
    if (chartData.labels && chartData.series) {
      if (Array.isArray(chartData.series[0])) {
        const header = [
          "Timestamp",
          ...(chartData.seriesNames ||
            chartData.series.map((_, i) => `Series ${i + 1}`)),
        ];
        const rows = chartData.labels.map((label, idx) => [
          label,
          ...chartData.series.map((s) => s[idx]),
        ]);
        return [header, ...rows];
      }
      return [
        ["Label", "Value"],
        ...chartData.labels.map((l, i) => [l, chartData.series[i]]),
      ];
    }

    if (Array.isArray(chartData)) return chartData;

    return null;
  };

  const handleExportCSV = () => {
    const data = prepareExportData();
    if (!data) return;

    const csvContent =
      "data:text/csv;charset=utf-8," +
      data.map((row) => row.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", getFileName("csv"));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsOpen(false);
  };

  const handleExportXLS = () => {
    const data = prepareExportData();
    if (!data) return;

    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chart Data");
    XLSX.writeFile(workbook, getFileName("xlsx"));
    setIsOpen(false);
  };

  const handleReset = () => {
    if (onReset) onReset();
    setIsOpen(false);
  };

  const handleFullscreen = () => {
    if (!chartRef?.current) return;
    if (chartRef.current.requestFullscreen) {
      chartRef.current.requestFullscreen();
    } else if (chartRef.current.webkitRequestFullscreen) {
      chartRef.current.webkitRequestFullscreen();
    }
    setIsOpen(false);
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: "8px",
          display: "flex",
          flexDirection: "column",
          gap: "3px",
          opacity: isOpen ? 1 : 0.6,
        }}
        aria-label="Chart menu"
      >
        <div style={{ width: "20px", height: "2px", background: "#64748b", borderRadius: "2px" }} />
        <div style={{ width: "20px", height: "2px", background: "#64748b", borderRadius: "2px" }} />
        <div style={{ width: "20px", height: "2px", background: "#64748b", borderRadius: "2px" }} />
      </button>

      {isOpen && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setIsOpen(false)}
          />
          <div
            ref={menuRef}
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              marginTop: "4px",
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              minWidth: "180px",
              zIndex: 1000,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
            }}
          >
            {onAIAnalyze && (
              <>
                <MenuItem
                  icon="✨"
                  label="AI Analyzer"
                  onClick={() => {
                    setIsOpen(false);
                    onAIAnalyze();
                  }}
                />
                <div style={{ height: "1px", background: "#e2e8f0", margin: "4px 0" }} />
              </>
            )}
            <MenuItem icon="🖼️" label="PNG" onClick={handleExportPNG} />
            <MenuItem icon="📷" label="JPG" onClick={handleExportJPG} />
            <MenuItem icon="📄" label="PDF" helpId="historical.exportImagePdf" onClick={handleExportPDF} />
            <MenuItem icon="📊" label="CSV" helpId="historical.exportCsv" onClick={handleExportCSV} disabled={!chartData} />
            <MenuItem icon="📗" label="XLS" helpId="historical.exportXls" onClick={handleExportXLS} disabled={!chartData} />
            {(chartType === "line" || chartType === "bar") && (
              <MenuItem icon="⚙️" label="Custom" helpId="historical.exportCustom" onClick={() => { setShowCustom(true); setIsOpen(false); }} />
            )}
            <div style={{ height: "1px", background: "#e2e8f0", margin: "4px 0" }} />
            <MenuItem icon="🔄" label="Reset View" onClick={handleReset} disabled={!onReset} />
            <MenuItem icon="⛶" label="Fullscreen" onClick={handleFullscreen} />
          </div>
        </>
      )}

      {showCustom && (
        <CustomExportModal
          onClose={() => setShowCustom(false)}
          chartTitle={chartTitle}
        />
      )}
    </div>
  );
};

const MenuItem = ({ icon, label, onClick, disabled = false, helpId }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      width: "100%",
      padding: "10px 16px",
      border: "none",
      background: "transparent",
      textAlign: "left",
      cursor: disabled ? "not-allowed" : "pointer",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      fontSize: "14px",
      color: disabled ? "#cbd5e1" : "#0f172a",
      transition: "background 0.15s",
      opacity: disabled ? 0.5 : 1,
    }}
    onMouseEnter={(e) => !disabled && (e.currentTarget.style.background = "#f8fafc")}
    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
  >
    <span style={{ fontSize: "16px" }}>{icon}</span>
    <span style={{ fontWeight: 500 }}>
      <FieldHelp id={helpId}>{label}</FieldHelp>
    </span>
  </button>
);

export default ChartBurgerMenu;
